<template>
  <v-radio-group v-model="value" :inline="inline" :rules="rules" hide-details="auto" class="e-radio-group">
    <v-radio v-for="(item, index) in items" :key="index" :label="item.title" :value="item.value" />
  </v-radio-group>
</template>

<script setup lang="ts" generic="T">
import { useVModel } from '@vueuse/core';
import type { InputRules } from '@/core/types/components/components';

const props = defineProps<{
  modelValue: T;
  items: {
    title: string;
    value: T;
  }[];
  inline?: boolean;
  rules?: InputRules;
}>();

const value = useVModel(props, 'modelValue');
</script>

<style scoped lang="scss">
.e-radio-group {
  :deep(.v-messages__message) {
    font-weight: 600;
    color: $alert-color;
  }

  :deep(.v-input__control) {
    --v-medium-emphasis-opacity: 1;

    .v-selection-control-group--inline {
      column-gap: 40px;
      row-gap: 18px;
    }

    .v-icon {
      --v-medium-emphasis-opacity: 1;

      font-size: 20px;
      color: transparent;

      background-color: $white-color;
      border: 1.5px solid $border-color;
      border-radius: 50%;

      transition: border-color $transition-duration;
    }

    .v-selection-control--dirty {
      .v-label {
        color: $text-primary-color !important;
      }
    }

    .v-selection-control {
      @include hover {
        .v-selection-control__wrapper {
          &:before {
            opacity: 1;
          }
        }

        .v-icon {
          border-color: $primary-color;
        }
      }
    }

    .mdi-radiobox-marked {
      background: url('@/assets/img/common/checbox-check.svg') no-repeat center center / 60%;
      border-color: $primary-color;
    }

    .v-label {
      font-size: 14px;
      line-height: 1;
      font-weight: 500;
      color: $text-secondary-color !important;

      transition: color $transition-duration;

      @include hover {
        color: $text-primary-color !important;
      }
    }
  }

  :deep(.v-selection-control__wrapper) {
    --v-selection-control-size: 24px;

    position: relative;

    margin-right: 8px;

    &:before {
      content: '';
      position: absolute;
      top: -2px;
      right: -2px;
      bottom: -2px;
      left: -2px;

      background-color: $primary-tonal-color;
      border-radius: 50%;

      opacity: 0;
      transition: opacity $transition-duration;
    }
  }

  &.v-input--disabled {
    .v-selection-control {
      color: $text-inactive-color;

      opacity: 1;

      :deep(.v-label) {
        color: inherit !important;
      }

      :deep(.v-icon) {
        background-color: $bg-inactive-color;
        border-color: $border-color-light;
      }

      :deep(.mdi-radiobox-marked) {
        background-image: url('@/assets/img/common/checbox-check--disabled.svg');
      }
    }

    .v-selection-control--dirty {
      color: $text-ordinary-color;
    }
  }
}
</style>
